import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Title, Paper, Stack, Group, Text, Select, Switch, Divider, ActionIcon
} from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import { PageContainer } from '../components/PageContainer';

const LANGUAGES = [
  { value: 'ru', label: 'Русский' },
  { value: 'en', label: 'English' },
];

const REGIONS = [
  { value: 'ru', label: 'Россия' },
  { value: 'us', label: 'United States' },
  { value: 'eu', label: 'Europe' },
];

const STORAGE_KEYS = {
  LANGUAGE: 'nre_language',
  REGION: 'nre_region',
  AUTO_DETECT_REGION: 'nre_auto_detect_region',
  HEARTBEAT_INTERVAL: 'nre_heartbeat_interval',
};

function detectRegion(): string {
  const locale = navigator.language || 'ru';
  if (locale.startsWith('ru')) return 'ru';
  if (locale.startsWith('en-US')) return 'us';
  return 'eu';
}

export function SettingsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [language, setLanguage] = useState(() => 
    localStorage.getItem(STORAGE_KEYS.LANGUAGE) || 'ru'
  );
  const [region, setRegion] = useState(() => 
    localStorage.getItem(STORAGE_KEYS.REGION) || detectRegion()
  );
  const [autoDetectRegion, setAutoDetectRegion] = useState(() => 
    localStorage.getItem(STORAGE_KEYS.AUTO_DETECT_REGION) !== 'false'
  );
  const [heartbeatInterval, setHeartbeatInterval] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.HEARTBEAT_INTERVAL);
    return stored ? parseInt(stored, 10) : 30;
  });

  useEffect(() => {
    if (autoDetectRegion) {
      const detected = detectRegion();
      setRegion(detected);
      localStorage.setItem(STORAGE_KEYS.REGION, detected);
    }
  }, [autoDetectRegion]);

  const handleLanguageChange = (value: string | null) => {
    if (!value) return;
    setLanguage(value);
    localStorage.setItem(STORAGE_KEYS.LANGUAGE, value);
    i18n.changeLanguage(value);
  };

  const handleRegionChange = (value: string | null) => {
    if (!value) return;
    setRegion(value);
    localStorage.setItem(STORAGE_KEYS.REGION, value);
  };

  const handleAutoDetectChange = (checked: boolean) => {
    setAutoDetectRegion(checked);
    localStorage.setItem(STORAGE_KEYS.AUTO_DETECT_REGION, String(checked));
    if (checked) {
      const detected = detectRegion();
      setRegion(detected);
      localStorage.setItem(STORAGE_KEYS.REGION, detected);
    }
  };

  const handleHeartbeatChange = (value: string | null) => {
    if (!value) return;
    const interval = parseInt(value, 10);
    setHeartbeatInterval(interval);
    localStorage.setItem(STORAGE_KEYS.HEARTBEAT_INTERVAL, String(interval));
  };

  return (
    <PageContainer>
      <Stack gap="md">
        <Group>
          <ActionIcon variant="subtle" size="lg" onClick={() => navigate(-1)} title={t('common.back')}>
            <IconArrowLeft size={20} />
          </ActionIcon>
          <Title order={2} style={{ fontSize: 'var(--font-size-lg)' }}>{t('settings.title')}</Title>
        </Group>

        <Paper p="md" withBorder>
          <Stack gap="md">
            <Text fw={500} style={{ fontSize: 'var(--font-size-md)' }}>{t('settings.language_section')}</Text>
            
            <Select
              label={t('settings.language')}
              data={LANGUAGES}
              value={language}
              onChange={handleLanguageChange}
            />

            <Divider />

            <Text fw={500} style={{ fontSize: 'var(--font-size-md)' }}>{t('settings.region_section')}</Text>

            <Switch
              label={t('settings.auto_detect_region')}
              checked={autoDetectRegion}
              onChange={(e) => handleAutoDetectChange(e.currentTarget.checked)}
            />

            <Select
              label={t('settings.region')}
              data={REGIONS}
              value={region}
              onChange={handleRegionChange}
              disabled={autoDetectRegion}
            />

            <Divider />

            <Text fw={500} style={{ fontSize: 'var(--font-size-md)' }}>{t('settings.connection_section')}</Text>

            <Select
              label={t('settings.heartbeat_interval')}
              description={t('settings.heartbeat_hint')}
              data={[
                { value: '15', label: '15 ' + t('settings.seconds') },
                { value: '30', label: '30 ' + t('settings.seconds') },
                { value: '60', label: '60 ' + t('settings.seconds') },
                { value: '120', label: '120 ' + t('settings.seconds') },
              ]}
              value={String(heartbeatInterval)}
              onChange={handleHeartbeatChange}
            />

            <Divider />

            <Text style={{ fontSize: 'var(--font-size-sm)' }} c="dimmed">
              {t('settings.region_hint')}
            </Text>
          </Stack>
        </Paper>
      </Stack>
    </PageContainer>
  );
}

export default SettingsPage;
